/**
 * Cart Protection System v2.0 - Newsletter Priority Orders
 * ========================================================
 * 
 * Clean, modular architecture for cart quantity restrictions
 * Replaces the complex 1,500+ line legacy system
 * 
 * Features:
 * - Single API batch call for efficiency
 * - Modular class design for maintainability
 * - Smart caching for performance
 * - Mobile-optimized user experience
 * - Comprehensive error handling
 */

// ===========================================
// STATE MANAGEMENT
// ===========================================

class CartProtectionState {
  constructor() {
    this.restrictions = new Map(); // productId -> restrictionData
    this.cartItems = new Map();    // itemId -> cartItem
    this.productGroups = new Map(); // productId -> [items]
    this.uiState = {
      initialized: false,
      loading: false,
      blocked: false
    };
    this.cache = new Map(); // productId -> cachedResponse
    this.cacheTimestamp = null;
  }

  setRestrictions(restrictions) {
    this.restrictions.clear();
    Object.entries(restrictions).forEach(([productId, data]) => {
      this.restrictions.set(productId, data);
      
      // CRITICAL DEBUG: Log each restriction being set with previousPurchases check
      console.log(`🔍 [DEBUG] Setting restriction for product ${productId}:`, {
        ...data,
        // CRITICAL: Highlight previousPurchases field
        previousPurchasesField: 'previousPurchases' in data ? data.previousPurchases : 'MISSING',
        allKeys: Object.keys(data)
      });
    });
  }

  getRestriction(productId) {
    return this.restrictions.get(productId.toString());
  }

  isRestricted(productId) {
    const restriction = this.getRestriction(productId);
    return restriction && restriction.isRestricted;
  }

  isBlocked(productId) {
    const restriction = this.getRestriction(productId);
    return restriction && restriction.isRestricted && !restriction.allowed;
  }
}

// ===========================================
// API COMMUNICATION
// ===========================================

class RestrictionAPI {
  constructor(config) {
    this.apiUrl = config.apiUrl || 'https://newsletter-priority-orders-minimal.vercel.app';
    this.timeout = config.timeout || 10000;
    this.debug = config.debug || false;
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[Cart Protection API] ${message}`, data || '');
    }
  }

  async batchCheckRestrictions(productIds, customerEmail, cartItems, retryCount = 0) {
    this.log('🔗 Batch checking restrictions', { 
      productCount: productIds.length, 
      hasEmail: !!customerEmail,
      attempt: retryCount + 1
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.apiUrl}/api/verify-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: customerEmail,
          productIds: productIds,
          batchMode: true,
          currentCartItems: cartItems,
          validationType: 'batch'
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = await response.json();
      
      this.log('✅ Batch API response', {
        success: result.success,
        processedProducts: result.processedProducts,
        restrictedCount: Object.values(result.restrictions || {}).filter(r => r.isRestricted).length
      });
      
      // DEBUG: Log detailed restrictions
      if (result.restrictions) {
        console.log('🔍 [DEBUG] Detailed batch restrictions:', result.restrictions);
      }

      // CRITICAL DEBUG: Log exact API responses to compare batch vs individual
      console.log('🔍 [BATCH API DEBUG] Complete response:', {
        url: this.apiUrl,
        requestBody: {
          email: customerEmail,
          productIds: productIds,
          validationType: 'batch',
          batchMode: true,
          currentCartItems: cartItems
        },
        fullResponse: result,
        restrictionsData: result.restrictions,
        productSpecificData: result.restrictions ? result.restrictions[productIds[0]] : null,
        // CRITICAL: Check if previousPurchases is in the batch response
        previousPurchasesInBatch: result.restrictions && result.restrictions[productIds[0]] ? 
          result.restrictions[productIds[0]].previousPurchases : 'NOT_FOUND'
      });

      // Log successful result but don't handle UI - let the calling class do that
      if (result.success && result.restrictions) {
        this.log('✅ Batch API response', {
          success: true,
          processedProducts: Object.keys(result.restrictions).length,
          restrictedCount: Object.values(result.restrictions).filter(r => r.isRestricted).length
        });
      } else {
        this.log('⚠️ Batch API failed', { success: false, error: 'No restrictions data' });
      }

      return result;

    } catch (error) {
      if (error.name === 'AbortError') {
        this.log('⏰ API request timed out');
      } else {
        console.error('[Cart Protection API] Batch check failed:', error);
      }
      
      // Retry logic for transient failures
      if (retryCount < 2 && (error.name === 'AbortError' || error.message.includes('Failed to fetch'))) {
        this.log(`🔄 Retrying batch check (attempt ${retryCount + 2}/3)`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return this.batchCheckRestrictions(productIds, customerEmail, cartItems, retryCount + 1);
      }
      
      // Return safe fallback after retries
      return {
        success: false,
        restrictions: {},
        error: error.message
      };
    }
  }

  async checkSingleProduct(productId, customerEmail, cartItems, quantity = 1) {
    this.log('🔗 Single product check', { productId, hasEmail: !!customerEmail });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.apiUrl}/api/verify-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: customerEmail,
          productId: productId,
          quantity: quantity,
          currentCartItems: cartItems,
          validationType: 'cart-info'
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = await response.json();
      
      this.log('✅ Single product API response', {
        productId,
        allowed: result.allowed,
        eligible: result.eligible
      });

      return result;
      
    } catch (error) {
      console.error('[Cart Protection API] Single check failed:', error);
      return {
        success: false,
        allowed: true, // Fail open for single product checks
        error: error.message
      };
    }
  }
}

// ===========================================
// CART MANAGEMENT
// ===========================================

class CartManager {
  constructor(debug = false) {
    this.debug = debug;
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[Cart Manager] ${message}`, data || '');
    }
  }

  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      this.log('📦 Cart data fetched', { 
        itemCount: cart.items.length,
        totalPrice: cart.total_price 
      });
      
      return cart;
    } catch (error) {
      console.error('[Cart Manager] Failed to fetch cart:', error);
      return { items: [] };
    }
  }

  groupItemsByProduct(cartItems) {
    const groups = new Map();
    
    cartItems.forEach(item => {
      const productId = item.product_id.toString();
      if (!groups.has(productId)) {
        groups.set(productId, []);
      }
      groups.get(productId).push(item);
    });

    this.log('🗂️ Items grouped by product', {
      productCount: groups.size,
      totalItems: cartItems.length
    });
    
    return groups;
  }

  calculateProductQuantity(productId, productItems = null) {
    if (productItems) {
      return productItems.reduce((sum, item) => sum + item.quantity, 0);
    }
    
    // Fallback: calculate from DOM
    const inputs = document.querySelectorAll(`input[data-product-id="${productId}"]`);
    let total = 0;
    inputs.forEach(input => {
      total += parseInt(input.value) || 0;
    });
    
    return total;
  }

  findInputElements(productId) {
    // Find all quantity inputs for this product
    const selectors = [
      `input[data-product-id="${productId}"]`,
      `input[data-line-item-key*="${productId}"]`,
      `input[name="updates[]"][data-product-id="${productId}"]`
    ];

    const inputs = [];
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (!inputs.includes(el)) {
          inputs.push(el);
        }
      });
    });

    this.log('🔍 Found input elements', { 
      productId, 
      inputCount: inputs.length 
    });
    
    return inputs;
  }

  async updateCartTotals() {
    this.log('🔄 Updating cart totals');
    
    // Trigger cart update events
    const events = ['cart:updated', 'cart:items_changed', 'cart:change', 'cart:refresh'];
    events.forEach(eventName => {
      const event = new CustomEvent(eventName, { bubbles: true });
      document.dispatchEvent(event);
    });

    // Try theme-specific updates
    if (window.cart?.refresh) {
      window.cart.refresh();
    }
    
    if (window.theme?.cart?.refresh) {
      window.theme.cart.refresh();
    }
  }
}

// ===========================================
// UI CONTROLLER
// ===========================================

class UIController {
  constructor(debug = false) {
    this.debug = debug;
    this.notifications = [];
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[UI Controller] ${message}`, data || '');
    }
  }

  applyRestrictions(restrictions, productGroups) {
    this.log('🎨 Applying restrictions to UI', { 
      restrictionCount: restrictions.size 
    });

    restrictions.forEach((restriction, productId) => {
      const productItems = productGroups.get(productId) || [];
      
      // DEBUG: Log detailed restriction application
      console.log(`🔍 [DEBUG] Applying restriction for product ${productId}:`, {
        productId,
        isRestricted: restriction.isRestricted,
        allowed: restriction.allowed,
        maxQuantity: restriction.maxQuantity,
        message: restriction.message,
        currentQuantity: productItems.reduce((sum, item) => sum + item.quantity, 0)
      });
      
      if (restriction.isRestricted) {
        if (restriction.allowed) {
          // CRITICAL FIX: Use remainingQuantity (accounts for previous purchases) instead of maxQuantity
          const effectiveLimit = restriction.remainingQuantity !== undefined 
            ? Math.max(0, restriction.remainingQuantity) 
            : restriction.maxQuantity;
          
          console.log('🔍 [DEBUG] Using effective limit for product validation:', {
            productId,
            maxQuantity: restriction.maxQuantity,
            remainingQuantity: restriction.remainingQuantity,
            effectiveLimit: effectiveLimit
          });
          
          this.enableProduct(productId, effectiveLimit, productItems);
          } else {
          this.blockProduct(productId, restriction.maxQuantity, restriction.message, productItems);
        }
      } else {
        this.enableProduct(productId, null, productItems);
      }
    });
  }

  blockProduct(productId, maxQuantity, message, productItems) {
    this.log('🚫 Blocking product', { productId, maxQuantity });

    const inputs = this.findAllInputsForProduct(productId);
    
    // Calculate total quantity already in cart for this product
    const totalQuantity = productItems.reduce((sum, item) => sum + item.quantity, 0);
    
    // Calculate remaining quantity available (for product-level limits)
    const remainingQuantity = maxQuantity ? Math.max(0, maxQuantity - totalQuantity) : 0;
    
    // DEBUG: Log calculation details
    console.log(`🔍 [DEBUG] Blocking product ${productId}:`, {
            productId,
      maxQuantity,
      totalQuantity,
            remainingQuantity,
      inputCount: inputs.length,
      productItems: productItems.map(item => ({ 
        id: item.id, 
        quantity: item.quantity, 
        variant_id: item.variant_id 
      }))
    });
    
    inputs.forEach(input => {
      input.setAttribute('data-npo-restricted', 'true');
      input.setAttribute('data-npo-blocked', 'true');
      input.setAttribute('data-max-quantity', remainingQuantity.toString());
      input.setAttribute('data-remaining-quantity', remainingQuantity.toString());
      
      // DON'T set browser max attribute to prevent "Value must be 0" popup
      // Our JavaScript validation will handle the restrictions
      input.removeAttribute('max');
      
      // If no remaining quantity, store current value for reference
      if (remainingQuantity === 0) {
        const currentValue = parseInt(input.value) || 0;
        input.setAttribute('data-current-value', currentValue.toString());
        // Don't actually disable - just prevent increases via validation
      }
      
      // Set up visual indicators
      this.addBlockedIndicator(input, message);
    });

    // Block checkout if any quantity violations
    if (maxQuantity && totalQuantity > maxQuantity) {
      this.blockCheckout(`${productItems[0]?.product_title || 'Product'} exceeds limit (${totalQuantity}/${maxQuantity})`);
    }
  }

  enableProduct(productId, maxQuantity, productItems) {
    this.log('✅ Enabling product', { productId, maxQuantity });

    const inputs = this.findAllInputsForProduct(productId);
    
    // Calculate total quantity already in cart for this product
    const totalQuantity = productItems.reduce((sum, item) => sum + item.quantity, 0);
    
    // Calculate remaining quantity available (for product-level limits)
    const remainingQuantity = maxQuantity ? Math.max(0, maxQuantity - totalQuantity) : null;
    
    inputs.forEach(input => {
      input.setAttribute('data-npo-restricted', maxQuantity ? 'true' : 'false');
      input.removeAttribute('data-npo-blocked');
      
      if (maxQuantity) {
        // DON'T set browser max attribute for purchase restrictions to prevent "Value must be 0" popup
        // Only use data attributes for our JavaScript validation
        input.removeAttribute('max'); // Remove any existing max attribute
        input.setAttribute('data-max-quantity', remainingQuantity.toString());
        input.setAttribute('data-remaining-quantity', remainingQuantity.toString());
        
        // Store the product-level limit for reference
        input.setAttribute('data-product-limit', maxQuantity.toString());
      } else {
        input.removeAttribute('max');
        input.removeAttribute('data-max-quantity');
        input.removeAttribute('data-remaining-quantity');
        input.removeAttribute('data-product-limit');
      }
      
      this.removeBlockedIndicator(input);
    });
  }

  findAllInputsForProduct(productId) {
    const selectors = [
      `input[data-product-id="${productId}"]`,
      `input[name="updates[]"][data-product-id="${productId}"]`
    ];

    const inputs = [];
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(input => {
        if (!inputs.includes(input)) {
          inputs.push(input);
        }
      });
    });

    return inputs;
  }

  addBlockedIndicator(input, message) {
    // Add visual indicator that this input is restricted
    const container = input.closest('.quantity, .cart-item, .line-item');
    if (container && !container.querySelector('.npo-restriction-indicator')) {
      const indicator = document.createElement('div');
      indicator.className = 'npo-restriction-indicator';
      indicator.style.cssText = `
        font-size: 12px;
        color: #e74c3c;
        margin-top: 4px;
        display: flex;
        align-items: center;
        gap: 4px;
      `;
      
      // Check if this is a product-level limit
      const productLimit = input.getAttribute('data-product-limit');
      const maxQuantity = input.getAttribute('data-max-quantity');
      const remainingQty = parseInt(maxQuantity) || 0;
      
      if (productLimit) {
        indicator.innerHTML = `
          <span>🔒</span>
          <span>Limited to ${productLimit} total across all variants (${remainingQty} remaining)</span>
        `;
        } else {
        indicator.innerHTML = `
          <span>🔒</span>
          <span>Limited quantity</span>
        `;
      }
      
      container.appendChild(indicator);
    }
  }

  removeBlockedIndicator(input) {
    const container = input.closest('.quantity, .cart-item, .line-item');
    if (container) {
      const indicator = container.querySelector('.npo-restriction-indicator');
      if (indicator) {
        indicator.remove();
      }
    }
  }

  showNotification(title, message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `npo-notification npo-notification-${type}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#fee' : '#e3f2fd'};
      border: 1px solid ${type === 'error' ? '#e74c3c' : '#2196f3'};
      border-left: 4px solid ${type === 'error' ? '#e74c3c' : '#2196f3'};
      border-radius: 6px;
      padding: 16px;
      max-width: 350px;
      z-index: 9999;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      cursor: pointer;
      font-family: Arial, sans-serif;
      font-size: 14px;
      line-height: 1.4;
      color: ${type === 'error' ? '#c0392b' : '#1976d2'};
    `;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <span style="font-size: 1.2em; margin-right: 8px;">${type === 'error' ? '⚠️' : '🎯'}</span>
        <strong>${title}</strong>
      </div>
      <div>${message}</div>
      <div style="font-size: 0.8em; color: #888; margin-top: 8px; font-style: italic;">
        Click to dismiss
      </div>
    `;

    document.body.appendChild(notification);
    this.notifications.push(notification);

    // Auto-remove
    setTimeout(() => {
      this.removeNotification(notification);
    }, 6000);

    // Click to dismiss
    notification.addEventListener('click', () => {
      this.removeNotification(notification);
    });

    this.log('📢 Notification shown', { title, type });
  }

  removeNotification(notification) {
    if (notification.parentElement) {
      notification.parentElement.removeChild(notification);
    }
    const index = this.notifications.indexOf(notification);
    if (index > -1) {
      this.notifications.splice(index, 1);
    }
  }

  blockCheckout(reason) {
    this.log('🚫 Blocking checkout', { reason });

    const checkoutButtons = document.querySelectorAll([
      'button[type="submit"]',
      'input[type="submit"]',
      '.checkout-button',
      '.cart-checkout',
      '.btn-checkout',
      '.checkout'
    ].join(', '));
    
    checkoutButtons.forEach(button => {
      button.setAttribute('data-npo-blocked', 'true');
      button.onclick = (e) => {
          e.preventDefault();
        e.stopPropagation();
        this.showNotification(
          'Please Adjust Quantities',
          'Some items exceed their limits. Please reduce quantities to continue.',
          'error'
        );
          return false;
      };
    });
  }

  unblockCheckout() {
    this.log('✅ Unblocking checkout');

    const blockedButtons = document.querySelectorAll('[data-npo-blocked="true"]');
    blockedButtons.forEach(button => {
      button.removeAttribute('data-npo-blocked');
      button.onclick = null;
    });
  }
}

// ===========================================
// EVENT MANAGEMENT
// ===========================================

class EventManager {
  constructor(cartManager, uiController, restrictionAPI, debug = false) {
    this.cartManager = cartManager;
    this.uiController = uiController;
    this.restrictionAPI = restrictionAPI;
    this.debug = debug;
    this.listeners = [];
    this.debounceTimeout = null;
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[Event Manager] ${message}`, data || '');
    }
  }

  setupInputListeners(customerEmail) {
    this.log('🎧 Input listeners disabled - script.cart.js handles all UI interactions');

    // NOTE: Input listeners are disabled to prevent conflicts with script.cart.js
    // All validation is now handled through the public API (validateQuantityChange)
    
    // Find all quantity inputs for reference
    const inputs = document.querySelectorAll('input[name="updates[]"], input[data-npo-restricted]');
    this.log(`📋 Found ${inputs.length} quantity inputs (UI handled by script.cart.js)`);
  }

  createInputChangeListener(input, customerEmail) {
    return (e) => {
      // Debounce to prevent excessive API calls
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.validateInput(input, customerEmail);
      }, 300);
    };
  }

  async validateInput(input, customerEmail) {
      const productId = input.getAttribute('data-product-id');
    const newQuantity = parseInt(input.value) || 0;
    const maxQuantity = parseInt(input.getAttribute('data-max-quantity')) || null;
    const productLimit = parseInt(input.getAttribute('data-product-limit')) || null;

    if (!productId) return;

    this.log('🔍 Validating input', { productId, quantity: newQuantity, maxQuantity, productLimit });

    // For multi-variant products, check total across all variants instead of individual remaining quantity
    const restriction = window.cartProtectionManager?.state?.getRestriction?.(productId);
    if (restriction && restriction.isRestricted) {
      // Calculate current total across all variants for this product
      const allVariantInputs = document.querySelectorAll(`input[data-product-id="${productId}"]`);
      let currentTotalAcrossVariants = 0;
      let newTotalAcrossVariants = 0;
      
      // Get the current quantity from this specific input (before change)
      const currentQuantityForThisInput = parseInt(input.defaultValue) || parseInt(input.getAttribute('data-original-value')) || 0;
      
      allVariantInputs.forEach(variantInput => {
        const variantCurrentValue = parseInt(variantInput.value) || 0;
        currentTotalAcrossVariants += variantCurrentValue;
        
        if (variantInput === input) {
          // This is the input being changed - use original value for current total
          newTotalAcrossVariants += newQuantity;
          currentTotalAcrossVariants = currentTotalAcrossVariants - variantCurrentValue + currentQuantityForThisInput;
        } else {
          // Other variant inputs - keep their current values
          newTotalAcrossVariants += variantCurrentValue;
        }
      });
      
      this.log('🔍 Multi-variant validation', {
        productId,
        newQuantity,
        currentQuantityForThisInput,
        restriction: restriction.maxQuantity,
        currentTotalAcrossVariants,
        newTotalAcrossVariants
      });
      
      // Check if new total would exceed the product limit
      if (newQuantity > currentQuantityForThisInput && newTotalAcrossVariants > restriction.maxQuantity) {
        input.value = currentQuantityForThisInput; // Reset to original value
        
        // 🎯 CENTRALIZED TOAST: Use toast manager for consistent messages
        const toastParams = this.toastManager.extractToastParams(restriction, currentTotalAcrossVariants, newTotalAcrossVariants);
        this.toastManager.showQuantityLimitToast(toastParams);
        return;
      }
      
      // If we got here, the change is allowed for multi-variant products
      return;
    }

    // Legacy single-variant validation (keep for compatibility)
    if (maxQuantity !== null && newQuantity > maxQuantity) {
      input.value = maxQuantity;
      
      // 🎯 CENTRALIZED TOAST: Use toast manager for legacy validation too
      if (productLimit) {
        this.toastManager.showQuantityLimitToast({
          maxQuantity: productLimit,
          previousPurchases: 0,
          currentInCart: maxQuantity,
          attemptedQuantity: newQuantity,
          isMultiVariant: true,
          reason: 'exceeds-limit'
        });
      } else {
        this.toastManager.showQuantityLimitToast({
          maxQuantity: maxQuantity,
          previousPurchases: 0,
          currentInCart: maxQuantity,
          attemptedQuantity: newQuantity,
          isMultiVariant: false,
          reason: 'exceeds-limit'
        });
      }
      return;
    }

    // Server validation for complex scenarios
    if (customerEmail && newQuantity > 0) {
      const cartData = await this.cartManager.fetchCartData();
      
      // Calculate current quantity in cart for this product
      const currentCartQuantity = cartData.items
        .filter(item => item.product_id && item.product_id.toString() === productId.toString())
        .reduce((sum, item) => sum + (item.quantity || 0), 0);

      // Calculate the delta (difference) - this is what we're adding/removing
      const quantityDelta = newQuantity - currentCartQuantity;
      
      this.log('🔍 Quantity calculation', {
        productId,
        currentInCart: currentCartQuantity,
        desiredTotal: newQuantity,
        delta: quantityDelta
      });

      // If delta is 0 or negative, no API validation needed
      if (quantityDelta <= 0) {
        return;
      }

      const result = await this.restrictionAPI.checkSingleProduct(
        productId, 
        customerEmail, 
        cartData.items, 
        quantityDelta  // Pass the delta, not the total
      );

      if (!result.success) {
        // API failed - show loading message and don't change input
        this.log('⚠️ Single product API failed, blocking change for safety');
        this.uiController.showNotification(
          'System Loading',
          'Product restrictions are still loading. Please try again in a moment.',
          'warning'
        );
        
        // Reset input to original value
        input.value = currentCartQuantity;
        return;
      }

      if (!result.allowed && result.restrictions?.[0]) {
        const restriction = result.restrictions[0];
        const remainingQuantity = restriction.remainingQuantity || 0;
        const maxAllowed = currentCartQuantity + remainingQuantity;
        
        input.value = maxAllowed;
        this.uiController.showNotification(
          'Purchase Limit',
          remainingQuantity > 0 ? 
            `You can only add ${remainingQuantity} more to your cart. Maximum total: ${maxAllowed}` : 
            'You have reached the maximum quantity for this product.',
          'error'
        );
      }
    }
  }

  setupCartUpdateListeners() {
    this.log('🎧 Setting up cart update listeners');

    const cartEvents = ['cart:updated', 'cart:items_changed', 'cart:change'];
    cartEvents.forEach(eventName => {
      const listener = () => {
        this.log(`🔄 Cart update detected: ${eventName}`);
        // Trigger re-validation after cart updates
        setTimeout(() => {
          this.validateAllQuantities();
        }, 500);
      };
      
      document.addEventListener(eventName, listener);
      this.listeners.push({ element: document, event: eventName, listener });
    });
  }

  async validateAllQuantities() {
    this.log('🔍 Validating all quantities');
    
    const restrictedInputs = document.querySelectorAll('input[data-npo-restricted="true"]');
    let hasViolations = false;

    restrictedInputs.forEach(input => {
      const quantity = parseInt(input.value) || 0;
      const maxQuantity = parseInt(input.getAttribute('data-max-quantity')) || null;
      
      if (maxQuantity && quantity > maxQuantity) {
        hasViolations = true;
      }
    });

    if (hasViolations) {
      this.uiController.blockCheckout('Quantity limits exceeded');
    } else {
      this.uiController.unblockCheckout();
    }
  }

  cleanup() {
    this.log('🧹 Cleaning up event listeners');
    
    this.listeners.forEach(({ element, event, listener }) => {
      element.removeEventListener(event, listener);
    });
    
    this.listeners = [];
    
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }
}

// ===========================================
// MAIN CART PROTECTION MANAGER
// ===========================================

class CartProtectionManager {
  constructor(config = {}) {
    // Prevent multiple instances - return existing instance regardless of initialization state
    if (window.cartProtectionManager) {
      console.log('[Cart Protection] 🔄 Returning existing CartProtectionManager instance');
      return window.cartProtectionManager;
    }

    this.config = {
      apiUrl: 'https://newsletter-priority-orders-minimal.vercel.app',
      debug: false,
      timeout: 10000,
      ...config
    };

    this.state = new CartProtectionState();
    this.api = new RestrictionAPI(this.config);
    this.cartManager = new CartManager(this.config.debug);
    this.uiController = new UIController(this.config.debug);
    this.eventManager = new EventManager(
      this.cartManager, 
      this.uiController, 
      this.api, 
      this.config.debug
    );

    this.toastManager = new QuantityToastManager(this.uiController); // CENTRALIZED TOAST SYSTEM

    // Pass state reference to cartManager for global access
    this.cartManager.state = this.state;

    this.customerEmail = null;
    this.initialized = false;

    // Queue system for changes while API is loading
    this.queuedChanges = [];
    this.processingQueue = false;

    // Make manager globally accessible for script.cart.js
    window.cartProtectionManager = this;

    this.log('🚀 Cart Protection Manager created');
  }

  log(message, data = null) {
    if (this.config.debug) {
      console.log(`[Cart Protection] ${message}`, data || '');
    }
  }

  getCustomerEmail() {
    // Try multiple sources for customer email
    const sources = [
      () => window.Shopify?.customer?.email,
      () => document.querySelector('[data-customer-email]')?.dataset.customerEmail,
      () => document.querySelector('meta[name="customer-email"]')?.content,
      () => window.theme?.customer?.email,
      () => window.cartNPOCustomerEmail
    ];

    for (const source of sources) {
      try {
        const email = source();
        if (email && email !== '' && email !== 'null') {
          return email;
        }
      } catch (error) {
        // Continue to next source
      }
    }

    this.log('⚠️ Could not find customer email');
    return null;
  }

  async initialize() {
    if (this.initialized) {
      this.log('Already initialized, skipping');
      return;
    }

    try {
      this.log('🔄 Initializing Cart Protection v2.0');
      this.state.uiState.loading = true;

      // Get customer email
      this.customerEmail = this.getCustomerEmail();
      this.log('👤 Customer email detected', { 
        hasEmail: !!this.customerEmail 
      });

      // Fetch current cart
      const cartData = await this.cartManager.fetchCartData();
      if (cartData.items.length === 0) {
        this.log('🛒 Empty cart, skipping initialization');
        this.initialized = true;
        return;
      }

      // Group cart items by product
      const productGroups = this.cartManager.groupItemsByProduct(cartData.items);
      this.state.productGroups = productGroups;

      // Store cart items in state
      cartData.items.forEach(item => {
        this.state.cartItems.set(item.id, item);
      });

      // Get all unique product IDs for batch check
      const productIds = Array.from(productGroups.keys());
      
      this.log('🔍 Checking restrictions for products', { 
        productCount: productIds.length 
      });

      // Batch check all restrictions
      const batchResult = await this.api.batchCheckRestrictions(
        productIds, 
        this.customerEmail, 
        cartData.items
      );

      if (batchResult.success && batchResult.restrictions) {
        // Store restrictions in state
        this.state.setRestrictions(batchResult.restrictions);
        
        // Apply restrictions to UI
        this.uiController.applyRestrictions(this.state.restrictions, productGroups);
        
        this.log('✅ Restrictions applied', {
          restrictedProducts: Object.values(batchResult.restrictions).filter(r => r.isRestricted).length,
          totalProducts: Object.keys(batchResult.restrictions).length
        });
      } else {
        this.log('⚠️ Batch restriction check failed, enabling fallback protection mode');
        this.enableFallbackProtection(productIds, productGroups);
      }

      // Set up event listeners
      this.eventManager.setupInputListeners(this.customerEmail);
      this.eventManager.setupCartUpdateListeners();

      this.state.uiState.loading = false;
      this.state.uiState.initialized = true;
      this.initialized = true;

      this.log('🎉 Cart Protection v2.0 initialized successfully');

      // Process any queued changes from while the system was loading
      if (this.queuedChanges.length > 0) {
        this.log(`🔄 Processing ${this.queuedChanges.length} queued changes`);
        // Process changes in the background
        setTimeout(() => {
          this.processQueuedChanges();
        }, 500); // Small delay to ensure UI is stable
      }

    } catch (error) {
      console.error('[Cart Protection] Initialization failed:', error);
      this.state.uiState.loading = false;
      // System remains disabled on error - fail safe
    }
  }

  // Public API for manual validation
  async validateProduct(productId, quantity = 1) {
    if (!this.initialized) {
      await this.initialize();
    }

    const cartData = await this.cartManager.fetchCartData();
    return await this.api.checkSingleProduct(
      productId, 
      this.customerEmail, 
      cartData.items, 
      quantity
    );
  }

  // Public API for getting restriction status
  getProductRestriction(productId) {
    return this.state.getRestriction(productId);
  }

  // ===========================================
  // QUEUE SYSTEM FOR LOADING PHASE
  // ===========================================

  /**
   * Queue a quantity change to be processed when the system is ready
   * @param {string} productId - The product ID
   * @param {number} newQuantity - The new quantity
   * @param {number} currentQuantity - The current quantity
   * @param {Function} callback - Callback to execute when processed
   */
  queueQuantityChange(productId, newQuantity, currentQuantity, callback = null) {
    const changeId = `${productId}-${Date.now()}`;
    const queuedChange = {
      id: changeId,
      productId,
      newQuantity,
      currentQuantity,
      timestamp: Date.now(),
      callback
    };
    
    this.queuedChanges.push(queuedChange);
    this.log(`📋 Queued quantity change`, {
      changeId,
      productId,
      newQuantity,
      currentQuantity,
      queueLength: this.queuedChanges.length
    });
    
    return changeId;
  }

  /**
   * Process a single queued change
   * @param {Object} queuedChange - The queued change object
   */
  async processQueuedChange(queuedChange) {
    const { id, productId, newQuantity, currentQuantity, callback } = queuedChange;
    
    this.log(`⚙️ Processing queued change`, { id, productId, newQuantity, currentQuantity });
    
    try {
      // Since we're processing queued changes, the system should be initialized
      // Do direct validation without recursion
      const restriction = this.state.getRestriction(productId);
      let validationResult;
      
      if (!restriction || !restriction.isRestricted) {
        // No restriction - allow
        validationResult = { allowed: true, reason: 'no-restriction' };
      } else {
        // Check against restriction limits
        const maxQuantity = restriction.maxQuantity;
        
        if (newQuantity > maxQuantity) {
          validationResult = {
            allowed: false,
            reason: 'exceeds-limit',
            message: `Maximum ${maxQuantity} allowed per customer`
          };
        } else if (restriction.allowed === false && newQuantity > currentQuantity) {
          // Check if this is a multi-variant product
          const allVariantInputs = document.querySelectorAll(`input[data-product-id="${productId}"]`);
          const isMultiVariant = allVariantInputs.length > 1;
          
          const baseMessage = `Maximum ${maxQuantity} allowed per customer. Limit reached.`;
          const multiVariantMessage = isMultiVariant ? 
            `Maximum ${maxQuantity} allowed per customer across all variants. You currently have ${maxQuantity} in your cart.` : 
            baseMessage;
          
          validationResult = {
            allowed: false,
            reason: 'not-allowed',
            message: multiVariantMessage
          };
        } else {
          validationResult = { allowed: true, reason: 'within-limits' };
        }
        
        // Debug info for multi-variant products
        if (restriction.isRestricted) {
          this.log('🔍 Queue validation debug', {
            productId,
            newQuantity,
            currentQuantity,
            maxQuantity,
            restrictionAllowed: restriction.allowed,
            restrictionRemainingQuantity: restriction.remainingQuantity,
            validationResult,
            isIncrease: newQuantity > currentQuantity
          });
        }
      }
      
      if (validationResult.allowed) {
        this.log(`✅ Queued change approved`, { id });
        
        // Execute the cart change
        if (callback && typeof callback === 'function') {
          this.log('🔄 Executing callback for queued change');
          callback(validationResult);
        } else {
          this.log('🔄 No callback provided, using direct cart API update');
          this.directCartUpdate(productId, newQuantity, currentQuantity);
        }
        
        this.showToastNotification(`Quantity updated to ${newQuantity}`, 'success');
      } else {
        this.log(`❌ Queued change rejected`, { id, reason: validationResult.reason });
        
        if (validationResult.message) {
          this.showToastNotification(validationResult.message, 'warning');
        } else {
          // Generic message for blocked changes
          this.showToastNotification('Product limit reached. Cannot increase quantity.', 'warning');
        }
      }
    } catch (error) {
      console.error(`[Cart Protection] Failed to process queued change ${id}:`, error);
      this.showToastNotification('Failed to process quantity change. Please try again.', 'error');
    }
  }

  /**
   * Direct cart update using Shopify API
   * @param {string} productId - The product ID  
   * @param {number} newQuantity - The new quantity
   * @param {number} currentQuantity - The current quantity (to identify correct variant)
   */
  async directCartUpdate(productId, newQuantity, currentQuantity = null) {
    try {
      this.log(`🔄 Direct cart update started`, { productId, newQuantity, currentQuantity });
      
      // Find the cart item with this product ID
      const cartData = await this.cartManager.fetchCartData();
      this.log(`📦 Cart data fetched`, { 
        totalItems: cartData.items.length,
        cartValue: cartData.total_price 
      });
      
      // Find the correct cart item
      let cartItem;
      
      if (currentQuantity !== null) {
        // For multi-variant products, find the specific item by product ID AND current quantity
        cartItem = cartData.items.find(item => 
          item.product_id && item.product_id.toString() === productId.toString() &&
          item.quantity === currentQuantity
        );
        
        if (!cartItem) {
          this.log(`❌ Cart item not found for product ${productId} with quantity ${currentQuantity}`, {
            availableItems: cartData.items.filter(item => 
              item.product_id && item.product_id.toString() === productId.toString()
            ).map(item => ({
              variantId: item.variant_id,
              quantity: item.quantity,
              cartLineItemId: item.key || item.id
            }))
          });
          return;
        }
        
        this.log(`🎯 Found specific cart item`, {
          targetQuantity: currentQuantity,
          variantId: cartItem.variant_id,
          cartLineItemId: cartItem.key || cartItem.id
        });
      } else {
        // Fallback: find first item with product ID (for backward compatibility)
        cartItem = cartData.items.find(item => 
          item.product_id && item.product_id.toString() === productId.toString()
        );
        
        if (!cartItem) {
          this.log(`❌ Cart item not found for product ${productId}`, {
            availableProductIds: cartData.items.map(item => item.product_id)
          });
          return;
        }
        
        this.log(`⚠️ Using fallback cart item selection (no currentQuantity provided)`);
      }
      
      // Use the correct cart line item ID (key property)
      const cartLineItemId = cartItem.key || cartItem.id;
      
      this.log(`📋 Cart item found`, {
        itemId: cartItem.id,
        cartLineItemId: cartLineItemId,
        variantId: cartItem.variant_id,
        currentQuantity: cartItem.quantity,
        targetQuantity: newQuantity,
        expectedCurrentQuantity: currentQuantity,
        quantityMatch: currentQuantity === null || cartItem.quantity === currentQuantity,
        productTitle: cartItem.product_title,
        variantTitle: cartItem.variant_title || 'No variant title'
      });
      
      // Update cart using Shopify API
      const requestBody = {
        id: cartLineItemId,
        quantity: newQuantity
      };
      
      this.log(`🚀 Sending cart update request`, requestBody);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      this.log(`📨 Cart API response`, {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
      
      if (response.ok) {
        const result = await response.json();
        this.log(`✅ Direct cart update successful`, { 
          itemId: cartItem.id, 
          newQuantity,
          totalPrice: result.total_price,
          itemCount: result.item_count
        });
        
        // Show success toast
        this.showToastNotification(`Cart updated: ${cartItem.product_title} quantity changed to ${newQuantity}`, 'success');
        
        // Reload the page to sync everything
        this.log('🔄 Reloading page to sync cart data');
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        const errorText = await response.text();
        this.log(`❌ Direct cart update failed`, { 
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        
        this.showToastNotification('Failed to update cart. Please try again.', 'error');
      }
      
    } catch (error) {
      console.error('[Cart Protection] Direct cart update failed:', error);
      this.log(`❌ Direct cart update exception`, {
        error: error.message,
        stack: error.stack
      });
      
      this.showToastNotification('Failed to update cart. Please try again.', 'error');
    }
  }

  /**
   * Process all queued changes
   */
  async processQueuedChanges() {
    if (this.processingQueue || this.queuedChanges.length === 0) {
      return;
    }
    
    this.processingQueue = true;
    const changesToProcess = [...this.queuedChanges]; // Copy array
    this.queuedChanges = []; // Clear original array
    
    this.log(`🔄 Processing ${changesToProcess.length} queued changes`);
    
    for (const queuedChange of changesToProcess) {
      await this.processQueuedChange(queuedChange);
      // Small delay between processing to avoid overwhelming the system
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.processingQueue = false;
    this.log(`✅ Finished processing queued changes`);
  }

  // Fallback protection when API is unavailable
  enableFallbackProtection(productIds, productGroups) {
    this.log('🛡️ Enabling fallback protection mode');
    
    // Look for client-side restriction data
    const fallbackRestrictions = {};
    
    productIds.forEach(productId => {
      const productItems = productGroups.get(productId) || [];
      const totalQuantity = productItems.reduce((sum, item) => sum + item.quantity, 0);
      
      // Look for DOM-based restriction indicators
      const inputs = document.querySelectorAll(`input[data-product-id="${productId}"]`);
      let maxQuantity = null;
      let hasRestriction = false;
      
      inputs.forEach(input => {
        const maxAttr = input.getAttribute('data-max-quantity');
        const isRestricted = input.getAttribute('data-npo-restricted') === 'true';
        
        if (isRestricted && maxAttr) {
          maxQuantity = parseInt(maxAttr);
          hasRestriction = true;
        }
      });
      
      // Look for restriction notices in the DOM
      const restrictionNotices = document.querySelectorAll(`.restriction-notice[data-product-id="${productId}"]`);
      restrictionNotices.forEach(notice => {
        const maxQtyText = notice.textContent.match(/max[^\d]*(\d+)/i);
        if (maxQtyText) {
          maxQuantity = parseInt(maxQtyText[1]);
          hasRestriction = true;
        }
      });
      
      // Create fallback restriction
      if (hasRestriction && maxQuantity) {
        const remainingQuantity = Math.max(0, maxQuantity - totalQuantity);
        const allowed = totalQuantity <= maxQuantity;
        
        fallbackRestrictions[productId] = {
          isRestricted: true,
          eligible: true,
          allowed: allowed,
          maxQuantity: maxQuantity,
          remainingQuantity: remainingQuantity,
          message: allowed ? 
            `Maximum ${maxQuantity} per customer` : 
            `Maximum ${maxQuantity} per customer exceeded`,
          fallbackMode: true
        };
        
        this.log(`🛡️ Fallback restriction created for product ${productId}`, {
          maxQuantity,
          totalQuantity,
          remainingQuantity,
          allowed
        });
      }
    });
    
    // Apply fallback restrictions
    if (Object.keys(fallbackRestrictions).length > 0) {
      this.state.setRestrictions(fallbackRestrictions);
      this.uiController.applyRestrictions(this.state.restrictions, productGroups);
      
      this.log('🛡️ Fallback protection applied', {
        protectedProducts: Object.keys(fallbackRestrictions).length,
        totalProducts: productIds.length
      });
      
      // Show user notification about fallback mode
      this.uiController.showNotification(
        'System Notice',
        'Cart protection is running in offline mode. Some features may be limited.',
        'warning'
      );
    } else {
      this.log('🛡️ No fallback restriction data found - basic protection enabled');
      this.enableBasicProtection(productIds, productGroups);
    }
  }
  
  // Basic protection when no restriction data is available
  enableBasicProtection(productIds, productGroups) {
    this.log('🛡️ Enabling basic protection mode');
    
    // Apply conservative limits based on cart contents
    const basicRestrictions = {};
    
    productIds.forEach(productId => {
      const productItems = productGroups.get(productId) || [];
      const totalQuantity = productItems.reduce((sum, item) => sum + item.quantity, 0);
      
      // If there are already items in cart, limit increases
      if (totalQuantity > 0) {
        basicRestrictions[productId] = {
          isRestricted: true,
          eligible: true,
          allowed: false,
          maxQuantity: totalQuantity,
          remainingQuantity: 0,
          message: 'Product restrictions could not be verified - changes blocked for safety',
          fallbackMode: true,
          basicMode: true
        };
        
        this.log(`🛡️ Basic protection applied to product ${productId}`, {
          currentQuantity: totalQuantity,
          changesAllowed: false
        });
      }
    });
    
    if (Object.keys(basicRestrictions).length > 0) {
      this.state.setRestrictions(basicRestrictions);
      this.uiController.applyRestrictions(this.state.restrictions, productGroups);
    }
  }

  // Fallback validation for single product when API is down
  validateWithFallback(productId, newTotalQuantity, currentCartQuantity) {
    this.log('🛡️ Performing fallback validation', {
      productId,
      newTotalQuantity,
      currentCartQuantity
    });
    
    // Check if we have cached restriction data
    const cachedRestriction = this.state.getRestriction(productId);
    if (cachedRestriction && cachedRestriction.isRestricted) {
      const maxQuantity = cachedRestriction.maxQuantity;
      const allowed = newTotalQuantity <= maxQuantity;
      
      return {
        allowed: allowed,
        maxAllowed: allowed ? newTotalQuantity : maxQuantity,
        message: allowed ? 
          `Maximum ${maxQuantity} per customer` : 
          `Maximum ${maxQuantity} per customer. Current limit: ${maxQuantity}`
      };
    }
    
    // Look for DOM-based restriction data
    const inputs = document.querySelectorAll(`input[data-product-id="${productId}"]`);
    let maxQuantity = null;
    
    inputs.forEach(input => {
      const maxAttr = input.getAttribute('data-max-quantity');
      const isRestricted = input.getAttribute('data-npo-restricted') === 'true';
      
      if (isRestricted && maxAttr) {
        maxQuantity = parseInt(maxAttr);
      }
    });
    
    if (maxQuantity) {
      const allowed = newTotalQuantity <= maxQuantity;
      
      return {
        allowed: allowed,
        maxAllowed: allowed ? newTotalQuantity : maxQuantity,
        message: allowed ? 
          `Maximum ${maxQuantity} per customer` : 
          `Maximum ${maxQuantity} per customer. Current limit: ${maxQuantity}`
      };
    }
    
    // Conservative fallback - don't allow increases if we can't verify
    const isIncrease = newTotalQuantity > currentCartQuantity;
    
    return {
      allowed: !isIncrease,
      maxAllowed: currentCartQuantity,
      message: isIncrease ? 
        'Product restrictions are still loading. Please try again in a moment.' : 
        'Product restrictions could not be verified'
    };
  }

  // Cleanup method
  destroy() {
    this.log('🧹 Destroying cart protection manager');
    this.eventManager?.cleanup();
    this.state = null;
    this.initialized = false;
  }

  // ===========================================
  // PUBLIC API FOR UI INTERACTIONS
  // ===========================================

  /**
   * Validates if a quantity change is allowed for a product
   * @param {string} productId - The product ID
   * @param {number} newQuantity - The new quantity for the specific variant
   * @param {number} currentQuantity - The current quantity for the specific variant
   * @returns {Object} - { allowed: boolean, reason: string, message?: string }
   */
  async validateQuantityChange(productId, newQuantity, currentQuantity) {
    console.log(`[Cart Protection API] Validating quantity change for product ${productId}: ${currentQuantity} → ${newQuantity}`);
    
    // If system not initialized, use secure queue-based approach
    if (!this.initialized) {
      this.log('🔄 System still loading, queuing change for secure processing');
      
      // Show loading message to user
      this.showToastNotification('Checking product restrictions...', 'info');
      
      // Queue the change for processing when system is ready
      this.queueQuantityChange(productId, newQuantity, currentQuantity, (result) => {
        if (result.allowed) {
          // If allowed, perform the cart update
          this.log('✅ Queued change approved - performing cart update');
          this.directCartUpdate(productId, newQuantity, currentQuantity);
        } else {
          // If not allowed, show the restriction message
          this.showToastNotification(result.message || 'Product limit exceeded', 'error');
        }
      });
      
      // Always block during loading for security
      return {
        allowed: false,
        reason: 'loading-queued',
        message: 'Checking product restrictions...'
      };
    }
    
    const restriction = this.state.getRestriction(productId);
    
    // No restriction found - allow change
    if (!restriction || !restriction.isRestricted) {
      return { allowed: true, reason: 'no-restriction' };
    }
    
    // CRITICAL FIX: Include previous purchases in total calculation
    const previousPurchases = restriction.previousPurchases || 0;
    
    console.log('🔍 [VALIDATION DEBUG] Previous purchases from restriction:', {
      restrictionPreviousPurchases: restriction.previousPurchases,
      previousPurchasesUsed: previousPurchases,
      hasField: 'previousPurchases' in restriction,
      restrictionKeys: Object.keys(restriction)
    });
    
    // Calculate real-time total across all variants
    const allVariantInputs = document.querySelectorAll(`input[data-product-id="${productId}"]`);
    let currentTotalAcrossVariants = 0;
    let newTotalAcrossVariants = 0;
    
    allVariantInputs.forEach(input => {
      const inputCurrentValue = parseInt(input.value) || 0;
      const inputCurrentQuantity = parseInt(input.defaultValue) || 0;
      
      if (Math.abs(inputCurrentValue - currentQuantity) < Math.abs(inputCurrentValue - inputCurrentQuantity)) {
        // This is likely the input being changed
        newTotalAcrossVariants += newQuantity;
        currentTotalAcrossVariants += currentQuantity;
      } else {
        // Other variant inputs - use their current values
        newTotalAcrossVariants += inputCurrentValue;
        currentTotalAcrossVariants += inputCurrentValue;
      }
    });
    
    // CRITICAL FIX: Add previous purchases to both totals
    const totalWithPreviousPurchases = newTotalAcrossVariants + previousPurchases;
    const currentTotalWithPreviousPurchases = currentTotalAcrossVariants + previousPurchases;
    
    console.log('🔍 [VALIDATION DEBUG] Real-time validation calculation', {
      productId,
      newQuantity,
      currentQuantity,
      maxQuantity: restriction.maxQuantity,
      currentTotalAcrossVariants,
      newTotalAcrossVariants,
      previousPurchases,
      currentTotalWithPreviousPurchases,
      totalWithPreviousPurchases,
      restrictionAllowed: restriction.allowed,
      variantCount: allVariantInputs.length,
      isMultiVariant: allVariantInputs.length > 1,
      shouldBlock: totalWithPreviousPurchases > restriction.maxQuantity,
      restrictionKeys: Object.keys(restriction)
    });
    
    // CRITICAL FIX: Block if trying to increase beyond the product limit (including previous purchases)
    if (newQuantity > currentQuantity && totalWithPreviousPurchases > restriction.maxQuantity) {
      // 🎯 CENTRALIZED TOAST: Use toast manager for consistent message generation
      const toastParams = this.toastManager.extractToastParams(restriction, currentTotalAcrossVariants, newTotalAcrossVariants);
      toastParams.reason = 'exceeds-limit-with-history';
      const message = this.toastManager.generateMessage(toastParams);
      
      return {
        allowed: false,
        reason: 'exceeds-limit-with-history',
        message: message
      };
    }
    
    // CRITICAL FIX: Block if restriction says not allowed (considering previous purchases)
    if (restriction.allowed === false && newQuantity > currentQuantity) {
      // 🎯 CENTRALIZED TOAST: Use toast manager for consistent message generation
      const toastParams = this.toastManager.extractToastParams(restriction, currentTotalAcrossVariants, newTotalAcrossVariants);
      toastParams.reason = 'not-allowed-with-history';
      const message = this.toastManager.generateMessage(toastParams);
      
      return {
        allowed: false,
        reason: 'not-allowed-with-history',
        message: message
      };
    }
    
    return { allowed: true, reason: 'within-limits' };
  }

  /**
   * Show toast notification to user
   * @param {string} message - The message to display
   * @param {string} type - The type of notification (error, warning, info, success)
   */
  showToastNotification(message, type = 'error') {
    // Anti-spam: prevent showing identical messages too quickly
    const now = Date.now();
    const messageKey = `${message}-${type}`;
    
    if (window.lastCartNotificationTime && window.lastCartNotificationMessage === messageKey) {
      if (now - window.lastCartNotificationTime < 2000) { // 2 second cooldown
        console.log('[Cart Protection] Suppressing duplicate notification:', message);
        return;
      }
    }
    
    window.lastCartNotificationTime = now;
    window.lastCartNotificationMessage = messageKey;
    
    // Remove any existing notification
    const existingNotification = document.querySelector('.cart-toast-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'cart-toast-notification';

    const colors = {
      success: '#28a745',
      error: '#dc3545',
      warning: '#fff3cd',
      info: '#e3f2fd'
    };

    const backgroundColor = colors[type] || colors.error;
    const textColor = type === 'warning' ? '#856404' : (type === 'info' ? '#1976d2' : 'white');
    const borderStyle = type === 'warning' ? 'border: 1px solid #ffeaa7;' : (type === 'info' ? 'border: 1px solid #2196f3;' : '');

    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${backgroundColor};
      color: ${textColor};
      ${borderStyle}
      padding: 12px 20px;
      border-radius: 5px;
      font-size: 14px;
      font-weight: 500;
      z-index: 999999;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      max-width: 350px;
      animation: cartToastSlideIn 0.3s ease-out;
      cursor: pointer;
      line-height: 1.4;
    `;

    notification.textContent = message;

    // Add CSS animation if not already present
    if (!document.getElementById('cart-toast-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'cart-toast-notification-styles';
      style.textContent = `
        @keyframes cartToastSlideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        /* Mobile-specific centering for toast notifications */
        @media (max-width: 768px) {
          .cart-toast-notification {
            left: 50% !important;
            right: auto !important;
            transform: translateX(-50%) !important;
            max-width: calc(100vw - 40px) !important;
            width: auto !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Add to page
    document.body.appendChild(notification);

    // Auto-remove after delay
    const delay = type === 'success' ? 3000 : 5000;
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, delay);

    // Add click to dismiss
    notification.addEventListener('click', () => {
      if (notification.parentElement) {
        notification.remove();
      }
    });
  }

  /**
   * Check if the system is ready for validation
   * @returns {boolean}
   */
  isReady() {
    return this.initialized;
  }

  /**
   * Get restriction data for a product
   * @param {string} productId - The product ID
   * @returns {Object|null} - The restriction data or null if not found
   */
  getRestriction(productId) {
    return this.state?.getRestriction(productId) || null;
  }
}

// =============================================================================
// 🎯 CENTRALIZED TOAST MANAGER - Single source of truth for all quantity notifications
// =============================================================================

class QuantityToastManager {
  constructor(uiController) {
    this.uiController = uiController;
    this.log = console.log.bind(console, '[Quantity Toast]');
  }

  /**
   * Generate consistent toast messages for quantity limit violations
   * @param {Object} params - Toast parameters
   * @param {number} params.maxQuantity - Maximum allowed quantity
   * @param {number} params.previousPurchases - Previously purchased quantity
   * @param {number} params.currentInCart - Current quantity in cart
   * @param {number} params.attemptedQuantity - Quantity user tried to add/set
   * @param {boolean} params.isMultiVariant - Whether this is a multi-variant product
   * @param {string} params.reason - Reason for blocking ('exceeds-limit', 'not-allowed', etc.)
   * @return {string} Formatted toast message
   */
  generateMessage({ maxQuantity, previousPurchases = 0, currentInCart, attemptedQuantity, isMultiVariant = false, reason }) {
    const totalWithPrevious = previousPurchases + currentInCart;
    const totalWithAttempt = previousPurchases + attemptedQuantity;
    
    this.log('🔍 Generating toast message:', {
      maxQuantity, previousPurchases, currentInCart, attemptedQuantity, 
      totalWithPrevious, totalWithAttempt, isMultiVariant, reason
    });

    // Base message structure
    const variantText = isMultiVariant ? ' across all variants' : '';
    const baseLimit = `Maximum ${maxQuantity} allowed per customer${variantText}.`;
    
    // Different messages based on situation
    if (reason === 'exceeds-limit' || reason === 'exceeds-limit-with-history') {
      if (previousPurchases > 0) {
        return `${baseLimit} You have reached your limit with ${totalWithPrevious} total (${previousPurchases} previous + ${currentInCart} in cart).`;
      } else {
        return `${baseLimit} You currently have ${currentInCart} in your cart.`;
      }
    }
    
    if (reason === 'not-allowed' || reason === 'not-allowed-with-history') {
      if (previousPurchases > 0) {
        return `${baseLimit} You have reached your limit with ${totalWithPrevious} total (${previousPurchases} previous + ${currentInCart} in cart).`;
      } else {
        return `${baseLimit} Limit reached.`;
      }
    }
    
    if (reason === 'remaining-exceeded') {
      const remaining = Math.max(0, maxQuantity - previousPurchases);
      if (previousPurchases > 0) {
        return `You can only add ${remaining} more to cart. You have already ordered ${previousPurchases}.`;
      } else {
        return `${baseLimit}`;
      }
    }

    // Fallback
    return `${baseLimit} You have reached your limit.`;
  }

  /**
   * Show quantity limit toast notification
   * @param {Object} params - Toast parameters (same as generateMessage)
   */
  showQuantityLimitToast(params) {
    const message = this.generateMessage(params);
    
    this.log('🍞 Showing toast:', {
      title: 'Quantity Limit',
      message,
      type: 'error',
      params
    });
    
    this.uiController.showNotification('Quantity Limit', message, 'error');
    return message;
  }

  /**
   * Extract restriction data for toast generation
   * @param {Object} restriction - Restriction object from API
   * @param {number} currentQuantity - Current quantity in cart for this product
   * @param {number} newQuantity - New quantity being attempted
   * @return {Object} Parameters for toast generation
   */
  extractToastParams(restriction, currentQuantity, newQuantity) {
    return {
      maxQuantity: restriction.maxQuantity || restriction.maxQuantityPerCustomer,
      previousPurchases: restriction.previousPurchases || 0,
      currentInCart: currentQuantity,
      attemptedQuantity: newQuantity,
      isMultiVariant: document.querySelectorAll(`input[data-product-id="${restriction.productId || ''}"]`).length > 1,
      reason: restriction.allowed === false ? 'not-allowed-with-history' : 'exceeds-limit-with-history'
    };
  }
}

// ===========================================
// LEGACY COMPATIBILITY LAYER
// ===========================================

// Legacy class name for backward compatibility with theme
class CartQuantityProtection {
  constructor() {
    // Initialize the new modular system
    this.manager = new CartProtectionManager({
      debug: true // Set to false in production
    });
    
    // Expose for backward compatibility
    this.initialized = false;
    this.initialize = this.initialize.bind(this);
    
    console.log('[NPO Cart Protection] v2.0 Legacy Compatibility Layer Initialized');
  }

  async initialize() {
    // Check if we're on a page with a cart
    if (!document.querySelector('form[action*="/cart"]') && 
        !document.querySelector('input[name="updates[]"]')) {
      console.log('[NPO Cart Protection] No cart detected, skipping initialization');
      this.initialized = true;
      return;
    }

    await this.manager.initialize();
    this.initialized = this.manager.initialized;
  }

  // Legacy methods for theme compatibility
  log(message, data) {
    this.manager.log(message, data);
  }

  async validateProduct(productId, quantity) {
    return await this.manager.validateProduct(productId, quantity);
  }

  getProductRestriction(productId) {
    return this.manager.getProductRestriction(productId);
  }

  destroy() {
    this.manager.destroy();
  }
}

// Create global instance for theme compatibility
// Note: The theme will call new CartQuantityProtection() and then initialize()
window.CartQuantityProtection = CartQuantityProtection;

// Auto-initialize for cart pages (since theme.liquid initialization was removed)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartQuantityProtection = new CartQuantityProtection();
    window.cartQuantityProtection.initialize();
    
    // Expose the manager globally for script.cart.js
    window.cartProtectionManager = window.cartQuantityProtection.manager;
  });
} else {
  window.cartQuantityProtection = new CartQuantityProtection();
  window.cartQuantityProtection.initialize();
  
  // Expose the manager globally for script.cart.js
  window.cartProtectionManager = window.cartQuantityProtection.manager;
} 